import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "@/components/motion";

const BLOCKS = [
  { x: 52, y: 350, w: 64, h: 170, o: 0.92 },
  { x: 128, y: 295, w: 56, h: 225, o: 0.78 },
  { x: 196, y: 385, w: 76, h: 135, o: 0.6 },
  { x: 284, y: 330, w: 52, h: 190, o: 0.88 },
  { x: 348, y: 275, w: 48, h: 245, o: 0.7 },
  { x: 408, y: 365, w: 56, h: 155, o: 0.5 },
];

const CROSSHAIRS = [
  { x: 300, y: 235 },
  { x: 65, y: 65 },
  { x: 455, y: 130 },
];

export const GeoArt = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <div ref={ref} className="relative" data-testid="hero-geo-art">
      <motion.div
        style={{ y }}
        className="relative ml-auto aspect-square w-full max-w-[560px]"
      >
        <svg
          viewBox="0 0 520 520"
          fill="none"
          className="h-full w-full"
          role="img"
          aria-label="Abstract architectural graphic"
        >
          {[...Array(7)].map((_, i) => (
            <g key={`grid-${i}`} stroke="#0B1A34" strokeOpacity="0.07">
              <line x1={65 * (i + 1)} y1="0" x2={65 * (i + 1)} y2="520" />
              <line x1="0" y1={65 * (i + 1)} x2="520" y2={65 * (i + 1)} />
            </g>
          ))}

          {BLOCKS.map((b, i) => (
            <motion.rect
              key={`block-${i}`}
              x={b.x}
              y={b.y}
              width={b.w}
              height={b.h}
              fill="#0B1A34"
              fillOpacity={b.o}
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.45 + i * 0.08, ease: EASE }}
            />
          ))}

          {[90, 140, 190].map((r, i) => (
            <motion.circle
              key={`ring-${r}`}
              cx="300"
              cy="235"
              r={r}
              stroke="#0B1A34"
              strokeOpacity={0.28 + i * 0.12}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: 0.35 + i * 0.16, ease: EASE }}
            />
          ))}

          <motion.circle
            cx="300"
            cy="235"
            r="115"
            stroke="#C6A15B"
            strokeWidth="1.5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.7, delay: 0.85, ease: EASE }}
          />

          <motion.g
            animate={{ rotate: 360 }}
            transition={{ duration: 90, ease: "linear", repeat: Infinity }}
            style={{ transformOrigin: "300px 235px" }}
          >
            <circle
              cx="300"
              cy="235"
              r="225"
              stroke="#C6A15B"
              strokeOpacity="0.5"
              strokeDasharray="2 14"
            />
          </motion.g>

          <motion.line
            x1="300"
            y1="28"
            x2="300"
            y2="112"
            stroke="#C6A15B"
            strokeWidth="1.5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
          />

          {CROSSHAIRS.map((c, i) => (
            <motion.g
              key={`cross-${i}`}
              stroke="#C6A15B"
              strokeWidth="1.25"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.25 + i * 0.15 }}
            >
              <line x1={c.x - 8} y1={c.y} x2={c.x + 8} y2={c.y} />
              <line x1={c.x} y1={c.y - 8} x2={c.x} y2={c.y + 8} />
            </motion.g>
          ))}

          <motion.rect
            x="292"
            y="12"
            width="16"
            height="16"
            fill="#C6A15B"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.35, ease: EASE }}
            style={{ transformOrigin: "300px 20px" }}
          />
        </svg>
      </motion.div>
    </div>
  );
};
